/**
 * EmailEditor — a full rich-text ("Teams/Outlook compose"-style) GUI for building
 * email template bodies. Built on TipTap (already in the app), headless so the
 * toolbar matches shadcn. POC scope is intentionally broad ("no features limited"):
 * stakeholders can prune the toolbar later by dropping buttons/extensions.
 *
 * Output is HTML. Text styling (color, highlight, font) is emitted as INLINE styles
 * (via TipTap's TextStyle marks) and highlight uses `background-color` rather than
 * `<mark>`, which keeps it email-safe — Outlook ignores classes/<style> and does
 * not render <mark>. The backend still inlines any remaining CSS on send.
 */
import * as React from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import DOMPurify from 'dompurify';
import {useEditor, EditorContent, type Editor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import {TableKit} from '@tiptap/extension-table';
import {TextStyleKit} from '@tiptap/extension-text-style';
import {Extension} from '@tiptap/core';
import {Plugin, PluginKey} from '@tiptap/pm/state';
import {Decoration, DecorationSet} from '@tiptap/pm/view';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  Code as CodeIcon,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Minus,
  RemoveFormatting,
  Baseline,
  Highlighter,
  Code2,
  Eye,
  Pencil,
} from 'lucide-react';
import {cn} from '@/lib/utils';
import {Button} from './button';
import {Input} from './input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

/**
 * Highlights merge variables ({{token}}) inline — the same visual treatment the
 * subject field uses — and turns off spell-check on them so they don't get red
 * squiggles. Purely decorative: the document text stays "{{token}}" so backend
 * substitution is unaffected.
 */
const VARIABLE_REGEX = /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g;
const VariableHighlight = Extension.create({
  name: 'variableHighlight',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('variableHighlight'),
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) return;
              VARIABLE_REGEX.lastIndex = 0;
              let match: RegExpExecArray | null;
              while ((match = VARIABLE_REGEX.exec(node.text)) !== null) {
                const from = pos + match.index;
                decorations.push(
                  Decoration.inline(from, from + match[0].length, {
                    class: 'od-email-var',
                    spellcheck: 'false',
                  }),
                );
              }
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^>]*)?>/i;

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Legacy plain-text bodies carry their layout in newlines. Loading one straight
 * into the HTML editor collapses the whitespace onto one line, so convert it:
 * blank lines become paragraphs, single newlines become <br>.
 */
const plainTextToHtml = (text: string): string =>
  text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('') || '<p></p>';

/** HTML bodies pass through; plain-text (legacy) bodies are converted. */
const toEditorContent = (value: string): string => {
  if (!value) return '';
  return HTML_TAG_REGEX.test(value) ? value : plainTextToHtml(value);
};

export interface EmailEditorHandle {
  /** Insert a merge variable (e.g. "{{participant_name}}") at the cursor. */
  insertVariable: (token: string) => void;
  focus: () => void;
  /** Current HTML. */
  getHTML: () => string;
}

interface EmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Called when the editor gains focus (e.g. to track the active field). */
  onFocus?: () => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  className?: string;
}

const FONT_FAMILIES: Array<{label: string; value: string}> = [
  {label: 'Default', value: ''},
  {label: 'Arial', value: 'Arial, Helvetica, sans-serif'},
  {label: 'Verdana', value: 'Verdana, Geneva, sans-serif'},
  {label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif'},
  {label: 'Georgia', value: 'Georgia, serif'},
  {label: 'Times New Roman', value: '"Times New Roman", Times, serif'},
  {label: 'Courier New', value: '"Courier New", Courier, monospace'},
];

const FONT_SIZES: Array<{label: string; value: string}> = [
  {label: 'Default', value: ''},
  {label: 'Small', value: '12px'},
  {label: 'Normal', value: '14px'},
  {label: 'Medium', value: '16px'},
  {label: 'Large', value: '20px'},
  {label: 'Huge', value: '28px'},
];

const BLOCK_TYPES: Array<{label: string; value: string}> = [
  {label: 'Normal', value: 'normal'},
  {label: 'Heading 1', value: '1'},
  {label: 'Heading 2', value: '2'},
  {label: 'Heading 3', value: '3'},
];

/** A single toolbar button, shaded while its mark/node is active. */
const TB = ({
  active,
  disabled,
  onClick,
  title,
  children,
}: Readonly<{
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}>) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    disabled={disabled}
    className={cn('h-7 w-7 p-0', active ? 'bg-slate-200 text-slate-900' : '')}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
  >
    {children}
  </Button>
);

const Divider = () => <div className="mx-0.5 h-5 w-px bg-slate-300" />;

const selectClass =
  'h-7 rounded border border-slate-200 bg-white px-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:opacity-60';

// Content styling for the editor + preview. OneData's base CSS resets list markers
// and link colors, so these are set explicitly (relying on `prose` alone leaves
// bullets/numbers and links unstyled — which looks like the features "don't work").
const CONTENT_STYLE = [
  '[&_a]:text-blue-600 [&_a]:underline',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-slate-600',
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold',
  '[&_p]:my-1 [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_hr]:my-3 [&_hr]:border-slate-300',
  '[&_table]:border-collapse [&_td]:border [&_td]:border-slate-300 [&_td]:p-1.5 [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:p-1.5',
  '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0 [&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2',
].join(' ');

const currentBlockValue = (editor: Editor | null): string => {
  if (editor?.isActive('heading', {level: 1})) return '1';
  if (editor?.isActive('heading', {level: 2})) return '2';
  if (editor?.isActive('heading', {level: 3})) return '3';
  return 'normal';
};

const EmailEditor = forwardRef<EmailEditorHandle, EmailEditorProps>(
  function EmailEditor(
    {
      value,
      onChange,
      onFocus,
      placeholder = 'Write your email…',
      disabled = false,
      minHeight = 260,
      className,
    },
    ref,
  ) {
    const [mode, setMode] = useState<'edit' | 'preview' | 'html'>('edit');
    const [showLink, setShowLink] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    // Forces toolbar active-state to re-read on every selection/transaction.
    const [, force] = useState(0);
    const editorInstanceRef = useRef<Editor | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // The last `value` we loaded into the editor, so external changes (tab switch)
    // re-sync but the editor's own edits don't trigger a content reset / cursor jump.
    const appliedValueRef = useRef<string | null>(null);

    // Insert dropped / pasted / picked images. POC: images are embedded as data
    // URLs so the editor + preview work immediately. For production these should
    // be uploaded to blob storage and referenced by URL — email clients (Gmail/
    // Outlook) strip or block large inline base64 images.
    const insertImageFiles = async (files: File[]) => {
      const ed = editorInstanceRef.current;
      if (!ed) return;
      for (const file of files) {
        try {
          const src = await readFileAsDataUrl(file);
          ed.chain().focus().setImage({src, alt: file.name}).run();
        } catch {
          /* skip unreadable file */
        }
      }
    };

    const editor = useEditor({
      extensions: [
        // StarterKit v3 bundles Bold/Italic/Underline/Strike/Code/Link/lists/etc.
        // Configure the bundled Link so it doesn't navigate away while editing and
        // always carries safe new-tab attributes.
        StarterKit.configure({
          link: {
            openOnClick: false,
            autolink: true,
            HTMLAttributes: {rel: 'noopener noreferrer', target: '_blank'},
          },
        }),
        Subscript,
        Superscript,
        TextStyleKit, // TextStyle + Color + BackgroundColor + FontFamily + FontSize + LineHeight
        TextAlign.configure({types: ['heading', 'paragraph']}),
        TaskList,
        TaskItem.configure({nested: true}),
        TableKit.configure({table: {resizable: true}}),
        Image.configure({
          inline: false,
          HTMLAttributes: {class: 'max-w-full h-auto rounded'},
        }),
        Placeholder.configure({
          placeholder,
          emptyEditorClass: 'is-editor-empty',
        }),
        VariableHighlight,
      ],
      content: toEditorContent(value),
      editable: !disabled,
      onUpdate: ({editor}) => onChange(editor.getHTML()),
      onFocus: () => onFocus?.(),
      onSelectionUpdate: () => force((n) => n + 1),
      onTransaction: () => force((n) => n + 1),
      editorProps: {
        attributes: {
          class: `prose prose-sm max-w-none font-sans text-slate-800 focus:outline-none px-4 py-3 ${CONTENT_STYLE} [&_.od-email-var]:rounded [&_.od-email-var]:bg-blue-50 [&_.od-email-var]:px-1 [&_.od-email-var]:py-0.5 [&_.od-email-var]:font-medium [&_.od-email-var]:text-blue-700`,
        },
        handleDrop: (_view, event) => {
          const files = Array.from(event.dataTransfer?.files || []).filter((f) =>
            f.type.startsWith('image/'),
          );
          if (files.length === 0) return false;
          event.preventDefault();
          void insertImageFiles(files);
          return true;
        },
        handlePaste: (_view, event) => {
          const files = Array.from(event.clipboardData?.files || []).filter((f) =>
            f.type.startsWith('image/'),
          );
          if (files.length === 0) return false;
          event.preventDefault();
          void insertImageFiles(files);
          return true;
        },
      },
    });

    useEffect(() => {
      editorInstanceRef.current = editor;
    }, [editor]);

    // Keep editor in sync when the bound value changes externally (e.g. switching
    // template tabs), while ignoring the editor's own edits (which would reset the
    // content and jump the cursor). A value equal to the editor's current HTML came
    // from our own onChange; anything else is an external change to re-load.
    useEffect(() => {
      if (!editor) return;
      if (value === appliedValueRef.current) return;
      if (value === editor.getHTML()) {
        appliedValueRef.current = value;
        return;
      }
      editor.commands.setContent(toEditorContent(value), {emitUpdate: false});
      appliedValueRef.current = value;
    }, [value, editor]);

    useEffect(() => {
      editor?.setEditable(!disabled);
    }, [disabled, editor]);

    useImperativeHandle(
      ref,
      () => ({
        insertVariable: (token: string) => {
          if (!editor) return;
          editor.chain().focus().insertContent(`${token} `).run();
        },
        focus: () => editor?.commands.focus(),
        getHTML: () => editor?.getHTML() ?? '',
      }),
      [editor],
    );

    // Preview must render what the editor shows: convert a legacy plain-text body
    // to HTML first (preserving line breaks), exactly as the editor does — otherwise
    // the newlines collapse and the preview looks like one run of text.
    const sanitizedHtml = useMemo(
      () => DOMPurify.sanitize(toEditorContent(value || '')),
      [value],
    );

    if (!editor) return null;

    const applyLink = () => {
      const url = linkUrl.trim();
      if (!url) {
        setShowLink(false);
        return;
      }
      const attrs = {href: url, target: '_blank', rel: 'noopener noreferrer'};
      if (editor.state.selection.empty) {
        // No selection: insert the link text (or the URL) carrying the link mark,
        // then a plain space so the link doesn't "stick" to whatever is typed next.
        const text = linkText.trim() || url;
        editor
          .chain()
          .focus()
          .insertContent([
            {type: 'text', text, marks: [{type: 'link', attrs}]},
            {type: 'text', text: ' '},
          ])
          .run();
      } else {
        // Apply the link to the current selection.
        editor.chain().focus().extendMarkRange('link').setLink(attrs).run();
      }
      setShowLink(false);
      setLinkUrl('');
      setLinkText('');
    };

    return (
      <div
        className={cn(
          'rounded-md border border-slate-200 bg-white',
          disabled && 'opacity-70',
          className,
        )}
      >
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 rounded-t-md border-b border-slate-200 bg-slate-50 p-1.5">
          {/* Block type */}
          <select
            className={selectClass}
            title="Text style"
            value={currentBlockValue(editor)}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'normal') editor.chain().focus().setParagraph().run();
              else
                editor
                  .chain()
                  .focus()
                  .toggleHeading({level: Number(v) as 1 | 2 | 3})
                  .run();
            }}
          >
            {BLOCK_TYPES.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>

          {/* Font family */}
          <select
            className={cn(selectClass, 'max-w-[110px]')}
            title="Font"
            disabled={disabled}
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (!v) editor.chain().focus().unsetFontFamily().run();
              else editor.chain().focus().setFontFamily(v).run();
            }}
          >
            <option value="" disabled hidden>
              Font
            </option>
            {FONT_FAMILIES.map((f) => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>

          {/* Font size */}
          <select
            className={selectClass}
            title="Font size"
            disabled={disabled}
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (!v) editor.chain().focus().unsetFontSize().run();
              else editor.chain().focus().setFontSize(v).run();
            }}
          >
            <option value="" disabled hidden>
              Size
            </option>
            {FONT_SIZES.map((s) => (
              <option key={s.label} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <Divider />

          <TB active={editor.isActive('bold')} disabled={disabled} title="Bold" onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('italic')} disabled={disabled} title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('underline')} disabled={disabled} title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <UnderlineIcon className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('strike')} disabled={disabled} title="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Strikethrough className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('subscript')} disabled={disabled} title="Subscript" onClick={() => editor.chain().focus().toggleSubscript().run()}>
            <SubscriptIcon className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('superscript')} disabled={disabled} title="Superscript" onClick={() => editor.chain().focus().toggleSuperscript().run()}>
            <SuperscriptIcon className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('code')} disabled={disabled} title="Inline code" onClick={() => editor.chain().focus().toggleCode().run()}>
            <CodeIcon className="h-3.5 w-3.5" />
          </TB>

          {/* Text color */}
          <label className="relative flex h-7 w-7 cursor-pointer items-center justify-center rounded hover:bg-slate-200" title="Text color">
            <Baseline className="h-3.5 w-3.5" />
            <input
              type="color"
              className="absolute inset-0 cursor-pointer opacity-0"
              disabled={disabled}
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />
          </label>
          {/* Highlight (email-safe background-color, not <mark>) */}
          <label className="relative flex h-7 w-7 cursor-pointer items-center justify-center rounded hover:bg-slate-200" title="Highlight color">
            <Highlighter className="h-3.5 w-3.5" />
            <input
              type="color"
              className="absolute inset-0 cursor-pointer opacity-0"
              disabled={disabled}
              onChange={(e) => editor.chain().focus().setBackgroundColor(e.target.value).run()}
            />
          </label>

          <Divider />

          <TB active={editor.isActive('bulletList')} disabled={disabled} title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <List className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('orderedList')} disabled={disabled} title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <ListOrdered className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('taskList')} disabled={disabled} title="Checklist" onClick={() => editor.chain().focus().toggleTaskList().run()}>
            <ListChecks className="h-3.5 w-3.5" />
          </TB>
          <TB active={editor.isActive('blockquote')} disabled={disabled} title="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <Quote className="h-3.5 w-3.5" />
          </TB>

          <Divider />

          <div className="flex items-center gap-0.5">
            <TB active={editor.isActive({textAlign: 'left'})} disabled={disabled} title="Align left" onClick={() => editor.chain().focus().setTextAlign('left').run()}>
              <AlignLeft className="h-3.5 w-3.5" />
            </TB>
            <TB active={editor.isActive({textAlign: 'center'})} disabled={disabled} title="Align center" onClick={() => editor.chain().focus().setTextAlign('center').run()}>
              <AlignCenter className="h-3.5 w-3.5" />
            </TB>
            <TB active={editor.isActive({textAlign: 'right'})} disabled={disabled} title="Align right" onClick={() => editor.chain().focus().setTextAlign('right').run()}>
              <AlignRight className="h-3.5 w-3.5" />
            </TB>
            <TB active={editor.isActive({textAlign: 'justify'})} disabled={disabled} title="Justify" onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
              <AlignJustify className="h-3.5 w-3.5" />
            </TB>
          </div>

          <Divider />

          <div className="flex items-center gap-0.5">
            <TB disabled={disabled} title="Insert link" onClick={() => {
              setLinkUrl(editor.getAttributes('link').href || '');
              setLinkText('');
              setShowLink(true);
            }}>
              <LinkIcon className="h-3.5 w-3.5" />
            </TB>
            <TB disabled={disabled} title="Insert image (upload, or drag & drop / paste)" onClick={() => fileInputRef.current?.click()}>
              <ImageIcon className="h-3.5 w-3.5" />
            </TB>
            <TB disabled={disabled} title="Insert table" onClick={() => editor.chain().focus().insertTable({rows: 3, cols: 3, withHeaderRow: true}).run()}>
              <TableIcon className="h-3.5 w-3.5" />
            </TB>
            <TB disabled={disabled} title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
              <Minus className="h-3.5 w-3.5" />
            </TB>
            <TB disabled={disabled} title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
              <RemoveFormatting className="h-3.5 w-3.5" />
            </TB>
          </div>

          {/* Mode switch pushed to the right */}
          <div className="ml-auto flex items-center gap-0.5">
            <TB active={mode === 'edit'} title="Edit" onClick={() => setMode('edit')}>
              <Pencil className="h-3.5 w-3.5" />
            </TB>
            <TB active={mode === 'preview'} title="Preview" onClick={() => setMode('preview')}>
              <Eye className="h-3.5 w-3.5" />
            </TB>
            <TB active={mode === 'html'} title="HTML source" onClick={() => setMode('html')}>
              <Code2 className="h-3.5 w-3.5" />
            </TB>
          </div>
        </div>

        {/* Body */}
        {mode === 'edit' && (
          <div
            style={{minHeight}}
            className="[&_.is-editor-empty:first-child::before]:pointer-events-none [&_.is-editor-empty:first-child::before]:float-left [&_.is-editor-empty:first-child::before]:h-0 [&_.is-editor-empty:first-child::before]:text-slate-400 [&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
          >
            <EditorContent editor={editor} />
          </div>
        )}
        {mode === 'preview' && (
          <div style={{minHeight}} className="bg-slate-50 p-4">
            <div className="mx-auto max-w-[640px] rounded border border-slate-200 bg-white p-6 shadow-sm">
              <div
                className={`prose prose-sm max-w-none ${CONTENT_STYLE}`}
                dangerouslySetInnerHTML={{__html: sanitizedHtml}}
              />
            </div>
            <p className="mt-2 text-center text-xs text-slate-400">
              Preview — merge variables like <code>{'{{participant_name}}'}</code> are replaced when the email is sent.
            </p>
          </div>
        )}
        {mode === 'html' && (
          <textarea
            style={{minHeight}}
            className="w-full resize-y rounded-b-md border-0 p-4 font-mono text-xs text-slate-700 focus:outline-none"
            value={value}
            disabled={disabled}
            spellCheck={false}
            onChange={(e) => {
              onChange(e.target.value);
              editor.commands.setContent(e.target.value, {emitUpdate: false});
            }}
          />
        )}

        <Dialog open={showLink} onOpenChange={setShowLink}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Insert link</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {editor.state.selection.empty && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Text to display</label>
                  <Input value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="Link text (optional)" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600">URL</label>
                <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" autoFocus />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLink(false)}>Cancel</Button>
              <Button onClick={applyLink}>Insert</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Hidden picker for the image button; drag-and-drop & paste also work. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length) void insertImageFiles(files);
            e.currentTarget.value = '';
          }}
        />
      </div>
    );
  },
);

export default EmailEditor;
